import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Grid,
    Card,
    CardContent,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { CreateCourierOrderRequest, Order, Customer } from '../../types';
import { courierService } from '../../services/courierService';
import { formatCurrencyBDT } from '../../utils/currency';
import { validateAndNormalizeBdPhone } from '../../utils/phone';

const schema = yup.object({
    invoice: yup.string().required('Invoice number is required'),
    recipient_name: yup.string().required('Recipient name is required').max(100, 'Maximum 100 characters'),
    recipient_phone: yup
        .string()
        .required('Phone number is required')
        .test(
            'bd-phone',
            'Phone number must start with 01 or +8801',
            (value) => !value || validateAndNormalizeBdPhone(value).valid,
        ),
    alternative_phone: yup
        .string()
        .optional()
        .test(
            'bd-phone',
            'Phone number must start with 01 or +8801',
            (value) => !value || validateAndNormalizeBdPhone(value).valid,
        ),
    recipient_email: yup.string().optional().email('Invalid email format'),
    recipient_address: yup.string().required('Address is required').max(250, 'Maximum 250 characters'),
    cod_amount: yup.number().required('COD amount is required').min(0, 'Must be non-negative'),
    note: yup.string().optional().max(255, 'Maximum 255 characters'),
    item_description: yup.string().optional().max(255, 'Maximum 255 characters'),
    total_lot: yup.number().optional().min(1, 'Must be at least 1'),
    delivery_type: yup.number().required('Delivery type is required'),
});

interface CourierOrderFormData extends CreateCourierOrderRequest {}

interface CourierOrderFormProps {
    open: boolean;
    onClose: () => void;
    onSuccess: (response: any) => void;
    order: Order;
    customer?: Customer | null;
}

export const CourierOrderForm: React.FC<CourierOrderFormProps> = ({
    open,
    onClose,
    onSuccess,
    order,
    customer,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<CourierOrderFormData>({
        resolver: yupResolver(schema) as any,
        defaultValues: {
            delivery_type: 0, // Default to home delivery
        },
    });

    // Populate form when order or customer changes
    useEffect(() => {
        if (open && order) {
            const mappedData = courierService.mapOrderToCourierData(order, customer);
            reset(mappedData);
        }
    }, [open, order, customer, reset]);

    const onSubmit = async (data: CourierOrderFormData) => {
        try {
            setLoading(true);
            setError('');

            // Booked through our API, which also saves the consignment on
            // the order in the same step.
            const response = await courierService.createCourierOrder(order.order_id, {
                ...data,
                recipient_phone:
                    validateAndNormalizeBdPhone(data.recipient_phone).normalized ??
                    data.recipient_phone,
                alternative_phone: data.alternative_phone
                    ? validateAndNormalizeBdPhone(data.alternative_phone).normalized
                    : data.alternative_phone,
            });
            onSuccess(response);
            onClose();
        } catch (err: any) {
            // The server's own explanation (e.g. Steadfast's field errors).
            const detail = err?.response?.data?.detail;
            setError(
                typeof detail === 'string'
                    ? detail
                    : err?.code === 'ECONNABORTED'
                      ? 'No answer in time. Before trying again, check on Steadfast\'s portal whether the parcel was booked.'
                      : err?.message || 'Failed to create courier order',
            );
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            reset();
            setError('');
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Create Courier Order
                <Typography variant="subtitle2" component="span" display="block" color="text.secondary">
                    Order #{order?.order_number}
                </Typography>
            </DialogTitle>

            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {/* Order Summary */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Order Summary
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Order Number:
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {order?.order_number}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Amount:
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {formatCurrencyBDT(order?.total_amount || 0)}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Customer:
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {customer
                                            ? `${customer.first_name} ${customer.last_name}`.trim()
                                            : 'Walk-in Customer'
                                        }
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    <Typography variant="h6" gutterBottom>
                        Courier Details
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Controller
                                name="invoice"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Invoice Number"
                                        error={!!errors.invoice}
                                        helperText={errors.invoice?.message}
                                        disabled={loading}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Controller
                                name="delivery_type"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth error={!!errors.delivery_type}>
                                        <InputLabel>Delivery Type</InputLabel>
                                        <Select
                                            {...field}
                                            label="Delivery Type"
                                            disabled={loading}
                                        >
                                            <MenuItem value={0}>Home Delivery</MenuItem>
                                            <MenuItem value={1}>Point Delivery / Hub Pick Up</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Controller
                                name="recipient_name"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Recipient Name"
                                        error={!!errors.recipient_name}
                                        helperText={errors.recipient_name?.message}
                                        disabled={loading}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Controller
                                name="recipient_phone"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Recipient Phone"
                                        error={!!errors.recipient_phone}
                                        helperText={errors.recipient_phone?.message}
                                        disabled={loading}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Controller
                                name="alternative_phone"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Alternative Phone (Optional)"
                                        error={!!errors.alternative_phone}
                                        helperText={errors.alternative_phone?.message}
                                        disabled={loading}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Controller
                                name="recipient_email"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Recipient Email (Optional)"
                                        error={!!errors.recipient_email}
                                        helperText={errors.recipient_email?.message}
                                        disabled={loading}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <Controller
                                name="recipient_address"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="Recipient Address"
                                        error={!!errors.recipient_address}
                                        helperText={errors.recipient_address?.message}
                                        disabled={loading}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Controller
                                name="cod_amount"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        type="number"
                                        label="COD Amount (BDT)"
                                        error={!!errors.cod_amount}
                                        helperText={errors.cod_amount?.message}
                                        disabled={loading}
                                        onChange={(e) => field.onChange(Number(e.target.value))}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <Controller
                                name="note"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        multiline
                                        rows={2}
                                        label="Delivery Note (Optional)"
                                        error={!!errors.note}
                                        helperText={errors.note?.message || `${field.value?.length || 0}/255`}
                                        disabled={loading}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <Controller
                                name="item_description"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        multiline
                                        rows={2}
                                        label="Item Description (Optional)"
                                        error={!!errors.item_description}
                                        helperText={errors.item_description?.message || `${field.value?.length || 0}/255`}
                                        disabled={loading}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={16} /> : null}
                    >
                        {loading ? 'Creating...' : 'Create Courier Order'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
